/**
 * CustomizationContext — إدارة ألوان وخط الموقع ديناميكياً من لوحة الأدمن.
 * يحمّل الإعدادات من /api/site-settings ويطبّقها كـ CSS variables.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../utils/api';

export interface SiteSettings {
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
  marketingContent: MarketingContent;
  legalContent: LegalContent;
  contactPageContent: ContactPageContent;
  faqContent: FaqContent;
  aboutContent: AboutContent;
  seoSettings: SeoSettings;
}

/** عنوان ووصف السيو لصفحة واحدة بلغة واحدة. لو فاضيين، الصفحة بترجع لعنوان/وصف
 *  افتراضي مبني من محتواها العادي (نفس السلوك قبل ما تُضاف لوحة السيو). */
export interface SeoLocaleEntry {
  title: string;
  description: string;
}

export interface SeoPageSettings {
  ar: SeoLocaleEntry;
  en: SeoLocaleEntry;
}

/** إعدادات السيو القابلة للتعديل من لوحة الأدمن لكل صفحة تسويقية رئيسية، بالعربي
 *  والإنجليزي. مفتاح كل صفحة مطابق لـ MarketingPageKey في src/utils/routes.ts. */
export interface SeoSettings {
  home: SeoPageSettings;
  about: SeoPageSettings;
  faq: SeoPageSettings;
  contact: SeoPageSettings;
  privacy: SeoPageSettings;
  terms: SeoPageSettings;
}

export interface MarketingPlanLocaleContent {
  eyebrow: string;
  name: string;
  chip: string;
  description: string;
  priceNote: string;
  creditsLabel: string;
  featuresTitle: string;
  features: string[];
  cta: string;
  note: string;
  popularLabel: string;
}

export interface MarketingUiLocaleContent {
  seoTitle: string;
  brandName: string;
  headerTagline: string;
  navLabels: string[];
  authenticatedPrimaryCta: string;
  authenticatedSecondaryCta: string;
  heroSecondaryCta: string;
  heroProof: string[];
  statsEyebrow: string;
  statsTitle: string;
  statsSubtitle: string;
  statsBadge: string;
  featuresEyebrow: string;
  advancedEyebrow: string;
  workflowEyebrow: string;
  pricingEyebrow: string;
  pricingTitle: string;
  pricingSubtitle: string;
  priceLabel: string;
  currencyLabel: string;
  unavailablePrice: string;
  pricingPlans: MarketingPlanLocaleContent[];
  pricingFootnote: string;
  finalEyebrow: string;
  finalPanelTitle: string;
  finalHighlights: string[];
  footerCtaTitle: string;
  footerCtaSubtitle: string;
  footerCtaButton: string;
  footerTagline: string;
  footerAssurance: string;
  footerPlatformTitle: string;
  footerPlatformLinks: string[];
  footerCompanyTitle: string;
  footerCompanyLinks: string[];
  footerContactTitle: string;
  footerContactDescription: string;
  footerWhatsappLabel: string;
  footerLegalLinks: string[];
  footerCopyright: string;
  footerMade: string;
}

export interface MarketingLocaleContent {
  badge: string;
  title: string;
  subtitle: string;
  primaryCta: string;
  secondaryCta: string;
  trustNote: string;
  featuresTitle: string;
  featuresSubtitle: string;
  features: Array<{ title: string; body: string }>;
  stats: Array<{ value: number; suffix: string; label: string }>;
  advancedTitle: string;
  advancedSubtitle: string;
  advancedFeatures: Array<{ title: string; body: string }>;
  workflowTitle: string;
  workflowSubtitle: string;
  workflow: Array<{ title: string; body: string }>;
  testimonialsTitle: string;
  testimonialsSubtitle: string;
  finalTitle: string;
  finalSubtitle: string;
  ui: MarketingUiLocaleContent;
}

/** قسم واحد ضمن صفحة الخصوصية/الشروط (عنوان + فقرة تمهيدية اختيارية + نقاط). */
export interface LegalSection {
  heading: string;
  body?: string;
  points?: string[];
}

export interface LegalLocaleContent {
  eyebrow: string;
  title: string;
  intro: string;
  updatedAt: string;
  privacyHeading: string;
  privacySections: LegalSection[];
  termsHeading: string;
  termsSections: LegalSection[];
  contactNote: string;
}

export interface ContactLocaleContent {
  eyebrow: string;
  title: string;
  subtitle: string;
  formEyebrow: string;
  formTitle: string;
  formSubtitle: string;
  inquiryTypes: string[];
  detailsEyebrow: string;
  detailsTitle: string;
  detailsSubtitle: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  successMessage: string;
  errorMessage: string;
}

/** سؤال واحد ضمن مجموعة أسئلة شائعة. */
export interface FaqQuestion {
  question: string;
  answer: string;
}

/** مجموعة أسئلة شائعة تحت عنوان قسم واحد (مثلاً "عن المنصة"، "الحساب والخصوصية"). */
export interface FaqGroup {
  eyebrow: string;
  heading: string;
  items: FaqQuestion[];
}

export interface FaqLocaleContent {
  eyebrow: string;
  title: string;
  subtitle: string;
  groups: FaqGroup[];
  ctaTitle: string;
  ctaSubtitle: string;
  ctaButton: string;
}

/** بطاقة قيمة واحدة في صفحة "من نحن" (مثل: الدقة، السرعة، فهم السوق). */
export interface AboutValue {
  title: string;
  body: string;
}

/** إحصائية واحدة تظهر في شريط الأرقام أسفل صفحة "من نحن". */
export interface AboutStat {
  value: string;
  label: string;
}

export interface AboutLocaleContent {
  eyebrow: string;
  title: string;
  intro: string;
  storyEyebrow: string;
  storyTitle: string;
  storyParagraphs: string[];
  teamTitle: string;
  teamBody: string;
  visionEyebrow: string;
  visionTitle: string;
  visionHeading: string;
  visionBody: string;
  missionHeading: string;
  missionBody: string;
  valuesEyebrow: string;
  valuesTitle: string;
  values: AboutValue[];
  stats: AboutStat[];
  ctaTitle: string;
  ctaSubtitle: string;
  ctaButton: string;
}

export interface MarketingContent {
  ar: MarketingLocaleContent;
  en: MarketingLocaleContent;
}

export interface LegalContent {
  ar: LegalLocaleContent;
  en: LegalLocaleContent;
}

export interface ContactPageContent {
  ar: ContactLocaleContent;
  en: ContactLocaleContent;
}

export interface FaqContent {
  ar: FaqLocaleContent;
  en: FaqLocaleContent;
}

export interface AboutContent {
  ar: AboutLocaleContent;
  en: AboutLocaleContent;
}

export const DEFAULT_MARKETING_CONTENT: MarketingContent = {
  ar: {
    badge: 'مساحة عمل ذكية للحملات العربية',
    title: 'من تفاصيل العرض إلى حملة قابلة للإطلاق والقياس.',
    subtitle: 'استهداف يجمع بناء الجمهور، النسخ الإعلانية، محاكاة الميزانية، حزمة الإطلاق ومراجعة العميل في مسار عمل واحد واضح.',
    primaryCta: 'ابدأ 3 حملات مجانًا',
    secondaryCta: 'تسجيل الدخول',
    trustNote: 'مصمم للمسوقين والوكالات وأصحاب الأنشطة في الأسواق العربية.',
    featuresTitle: 'كل ما تحتاجه لاتخاذ قرار حملة أفضل',
    featuresSubtitle: 'بدل التنقل بين ملفات وأدوات منفصلة، تتحول بياناتك إلى خطة عملية يمكن مراجعتها وتطويرها وقياسها.',
    features: [
      { title: 'استراتيجية حسب المجال', body: 'أسئلة واستهداف وسياسات وتقديرات تتكيف مع نوع النشاط وهدف الحملة.' },
      { title: 'محتوى قابل للتحرير', body: 'بدائل إعلانية، مقارنة بين الزوايا، إعادة صياغة وإصدارات محفوظة.' },
      { title: 'من الخطة إلى الإطلاق', body: 'محاكاة ميزانية، روابط UTM، قائمة فحص وبوابة مراجعة للعميل.' },
    ],
    stats: [
      { value: 1200, suffix: '+', label: 'حملة مُولَّدة' },
      { value: 87, suffix: '%', label: 'دقة تقدير CPL' },
      { value: 10, suffix: '+', label: 'مجالات مدعومة' },
      { value: 3, suffix: 'x', label: 'توفير في وقت الإعداد' },
    ],
    advancedTitle: 'كل أداة تحل مشكلة حقيقية',
    advancedSubtitle: 'أدوات مصممة بناءً على متطلبات المسوقين والوكالات في الأسواق العربية، تشمل مميزات لا توجد في أي أداة مجاورة.',
    advancedFeatures: [
      { title: 'استهداف متعدد الشرائح', body: 'هيكل حملة يحاكي Meta Ads Manager: واسع + اهتمامات + إعادة استهداف — كل شريحة بميزانيتها ورسالتها.' },
      { title: 'مدقق سياسات قبل الإطلاق', body: 'فحص آلي لكل نسخة إعلانية: ادعاءات مضمونة، Housing Category، الوعود الصحية — مع اقتراح صياغة بديلة.' },
      { title: 'محاكي اقتصاديات الحملة', body: 'حساب CPL وROI والعائد المتوقع من بيانات هامش الربح ومعدل التحويل — بدون استدعاء AI.' },
      { title: 'مصفوفة اختبارات A/B', body: 'حتى 18 توليفة جمهور × رسالة × صيغة إعلانية مع درجة ترشيح تقديرية لكل توليفة.' },
      { title: 'مساحة فريق متكاملة', body: 'تتبع استهلاك رصيد كل عضو، تعيين الحملات، تعليقات الفريق واعتمادات العميل في مكان واحد.' },
      { title: 'معايرة من بيانات حقيقية', body: 'كل حملة جديدة تستفيد من نتائج حملاتك السابقة في نفس المجال والمنطقة لتحسين دقة التقدير.' },
    ],
    workflowTitle: 'خمس مراحل من البيانات إلى الاعتماد',
    workflowSubtitle: 'كل مرحلة لها هدف واحد وقرار واضح، ويمكن فتحها ومشاركتها عبر رابط مستقل.',
    workflow: [
      { title: 'أدخل البيانات', body: 'أسئلة تتغير حسب المجال والهدف — عقارات أو تجارة إلكترونية أو صحة أو تعليم وغيرها.' },
      { title: 'راجع الافتراضات', body: 'تأكد من مصادر كل عنصر في الاستراتيجية قبل استهلاك الرصيد.' },
      { title: 'قارن البدائل', body: 'استهداف ونسخ إعلانية وزوايا قابلة للتحرير والإعادة.' },
      { title: 'جهّز الإطلاق', body: 'محاكاة وروابط UTM وقائمة فحص وحزمة إطلاق كاملة.' },
      { title: 'اعتمد وقِس', body: 'مراجعة العميل وإصدارات ونتائج فعلية وتوصيات تلقائية.' },
    ],
    testimonialsTitle: 'مسوقون يثقون في استهداف',
    testimonialsSubtitle: 'آراء المستخدمين',
    finalTitle: 'ابنِ حملتك التالية على بيانات أوضح.',
    finalSubtitle: 'ابدأ مجانًا، واحتفظ بكل حملة ونسخة وقرار في مساحة عمل واحدة.',
    ui: {
      seoTitle: 'استهداف | ذكاء الحملات متعدد المجالات',
      brandName: 'استهداف',
      headerTagline: 'ذكاء الحملات',
      navLabels: ['المزايا', 'الأدوات المتقدمة', 'كيف يعمل', 'الأسعار', 'من نحن', 'الأسئلة الشائعة', 'اتصل بنا'],
      authenticatedPrimaryCta: 'إنشاء حملة جديدة',
      authenticatedSecondaryCta: 'مساحة العمل',
      heroSecondaryCta: 'كيف تعمل الأداة',
      heroProof: ['٣ حملات مجانية', 'بدون بطاقة بنكية', 'نتيجة أولية خلال دقائق'],
      statsEyebrow: 'أثر يمكن قياسه',
      statsTitle: 'أرقام تعكس سرعة وجودة سير العمل',
      statsSubtitle: 'مؤشرات مختصرة تساعدك على فهم قيمة المنصة قبل بدء أول حملة.',
      statsBadge: 'مؤشرات مباشرة',
      featuresEyebrow: 'منصة واحدة',
      advancedEyebrow: 'أدوات متقدمة',
      workflowEyebrow: 'مسار واضح',
      pricingEyebrow: 'أسعار واضحة',
      pricingTitle: 'ابدأ مجانًا ثم توسّع عند الحاجة',
      pricingSubtitle: 'الأسعار وعدد العمليات تُحدَّث تلقائياً — لا تجديد قسري، لا رسوم خفية.',
      priceLabel: 'السعر',
      currencyLabel: 'ج.م',
      unavailablePrice: '—',
      pricingPlans: [
        {
          eyebrow: 'للاستكشاف', name: 'الباقة المجانية', chip: 'ابدأ هنا',
          description: 'جرّب سير العمل الكامل وأنشئ أول حملاتك دون أي التزام.',
          priceNote: 'مجانية دائمًا للبدء', creditsLabel: '{count} حملات مجانية',
          featuresTitle: 'ما تحصل عليه',
          features: ['حفظ ومراجعة الحملات', 'محاكي الميزانية', 'حزمة الإطلاق', 'نسخ إعلانية قابلة للتحرير'],
          cta: 'ابدأ مجانًا', note: 'لا تحتاج إلى بطاقة بنكية', popularLabel: '',
        },
        {
          eyebrow: 'للمسوقين وأصحاب الأعمال', name: 'Pro', chip: '',
          description: 'رصيد مرن وأدوات متقدمة لبناء حملات احترافية قابلة للقياس.',
          priceNote: 'دفعة واحدة، دون تجديد تلقائي', creditsLabel: '{count} عملية ذكاء اصطناعي',
          featuresTitle: 'كل ما في المجانية، بالإضافة إلى',
          features: ['إعادة صياغة متقدمة برصيد مرن', 'تحليلات ومحاكاة كاملة', 'مدقق سياسات Meta', 'معايرة من بيانات حقيقية', 'بوابة مراجعة العميل'],
          cta: 'اختر باقة Pro', note: 'أفضل قيمة للاستخدام الفردي', popularLabel: 'الأكثر اختيارًا',
        },
        {
          eyebrow: 'للفرق والوكالات', name: 'Agency', chip: 'للعمل الجماعي',
          description: 'مساحة موحدة لإدارة الفريق والعملاء والاعتمادات على نطاق أكبر.',
          priceNote: 'دفعة واحدة، مع تحكم كامل في الرصيد', creditsLabel: '{count} عملية قابلة للإدارة',
          featuresTitle: 'كل ما في Pro، بالإضافة إلى',
          features: ['مساحة فريق واعتمادات', 'تتبع استهلاك كل عضو', 'مصفوفة اختبارات A/B', 'رصيد Agency موسّع وقابل للإدارة', 'دعم أولوية'],
          cta: 'ابدأ كوكالة', note: 'مصممة لإدارة عدة أعضاء وعملاء', popularLabel: '',
        },
      ],
      pricingFootnote: 'يمكنك البدء مجانًا والانتقال إلى أي باقة لاحقًا دون فقد حملاتك المحفوظة.',
      finalEyebrow: 'خطوتك التالية',
      finalPanelTitle: 'جاهز خلال دقائق',
      finalHighlights: ['سياسات Meta مُدمجة', 'بلا بطاقة ائتمان', '٣ حملات مجانية', 'نتائج فورية'],
      footerCtaTitle: 'خطط بوضوح. أطلق بثقة.',
      footerCtaSubtitle: 'منصة واحدة لتحويل تفاصيل عرضك إلى حملة قابلة للمراجعة والقياس.',
      footerCtaButton: 'ابدأ مجانًا',
      footerTagline: 'ذكاء الحملات العربية',
      footerAssurance: 'مخرجات قابلة للمراجعة قبل الإطلاق',
      footerPlatformTitle: 'المنصة',
      footerPlatformLinks: ['الرئيسية', 'المزايا', 'الأدوات المتقدمة', 'الأسعار', 'إنشاء حساب', 'تسجيل الدخول'],
      footerCompanyTitle: 'الشركة',
      footerCompanyLinks: ['من نحن', 'الأسئلة الشائعة', 'اتصل بنا', 'سياسة الخصوصية', 'الشروط والأحكام'],
      footerContactTitle: 'تواصل معنا',
      footerContactDescription: 'فريقنا جاهز لمساعدتك في اختيار المسار الأنسب لحملتك.',
      footerWhatsappLabel: 'تواصل عبر واتساب',
      footerLegalLinks: ['الخصوصية', 'الشروط', 'الدعم'],
      footerCopyright: '© {year} استهداف. جميع الحقوق محفوظة.',
      footerMade: 'صُنع بخبرات مصرية',
    },
  },
  en: {
    badge: 'AI workspace for Arabic campaigns',
    title: 'Turn an offer brief into a campaign ready to launch and measure.',
    subtitle: 'Istihdaf brings audience planning, ad copy, budget simulation, launch assets, and client review into one focused workflow.',
    primaryCta: 'Start 3 campaigns free',
    secondaryCta: 'Sign in',
    trustNote: 'Built for marketers, agencies, and growing businesses across Arabic-speaking markets.',
    featuresTitle: 'Everything needed for a better campaign decision',
    featuresSubtitle: 'Move from scattered documents and tools to a practical plan you can review, improve, and measure.',
    features: [
      { title: 'Industry-aware strategy', body: 'Questions, targeting, policies, and estimates adapt to the business and campaign goal.' },
      { title: 'Editable campaign copy', body: 'Compare angles, rewrite individual fields, and keep every approved version.' },
      { title: 'Plan through launch', body: 'Budget scenarios, UTM links, launch checks, and a client review portal.' },
    ],
    stats: [
      { value: 1200, suffix: '+', label: 'Campaigns generated' },
      { value: 87, suffix: '%', label: 'CPL estimate accuracy' },
      { value: 10, suffix: '+', label: 'Industries supported' },
      { value: 3, suffix: 'x', label: 'Faster brief preparation' },
    ],
    advancedTitle: 'Every tool solves a real problem',
    advancedSubtitle: 'Tools built on real needs from marketers and agencies in Arabic-speaking markets — features you won\'t find anywhere else.',
    advancedFeatures: [
      { title: 'Multi-segment targeting', body: 'Campaign structure that mirrors Meta Ads Manager: broad + interest + retargeting — each with its own budget and message.' },
      { title: 'Pre-launch policy checker', body: 'Automated review of every ad copy: guaranteed claims, Housing Category, health promises — with suggested alternatives.' },
      { title: 'Campaign economics simulator', body: 'Calculate CPL, ROI, and projected returns from your profit margin and conversion rate — no AI call needed.' },
      { title: 'A/B test matrix', body: 'Up to 18 combinations of audience × message × format, each with an estimated recommendation score.' },
      { title: 'Full team workspace', body: 'Track each member\'s credit usage, assign campaigns, team comments, and client approvals in one place.' },
      { title: 'Calibration from real data', body: 'Every new campaign learns from your past results in the same industry and region to improve estimate accuracy.' },
    ],
    workflowTitle: 'Five stages from brief to approval',
    workflowSubtitle: 'Every stage has one purpose, one clear decision, and its own shareable URL.',
    workflow: [
      { title: 'Add the brief', body: 'Questions adapt to the industry and goal — real estate, e-commerce, health, education, and more.' },
      { title: 'Review assumptions', body: 'Confirm the source of every strategy element before using a credit.' },
      { title: 'Compare options', body: 'Editable targeting, copy, and campaign angles — rewrite any field.' },
      { title: 'Prepare launch', body: 'Budget scenarios, UTM links, launch checks, and a full launch package.' },
      { title: 'Approve and measure', body: 'Client review portal, version history, actual results, and auto-recommendations.' },
    ],
    testimonialsTitle: 'Marketers who trust Istihdaf',
    testimonialsSubtitle: 'User reviews',
    finalTitle: 'Build your next campaign on clearer inputs.',
    finalSubtitle: 'Start free and keep every campaign, version, and decision in one workspace.',
    ui: {
      seoTitle: 'Istihdaf | AI Campaign Intelligence',
      brandName: 'Istihdaf',
      headerTagline: 'Campaign intelligence',
      navLabels: ['Features', 'Advanced tools', 'Workflow', 'Pricing', 'About', 'FAQ', 'Contact'],
      authenticatedPrimaryCta: 'Create a campaign',
      authenticatedSecondaryCta: 'Workspace',
      heroSecondaryCta: 'How it works',
      heroProof: ['3 free campaigns', 'No credit card', 'First result in minutes'],
      statsEyebrow: 'Measurable impact',
      statsTitle: 'Numbers that reflect a faster, clearer workflow',
      statsSubtitle: 'A concise view of the value you get before launching your first campaign.',
      statsBadge: 'Live indicators',
      featuresEyebrow: 'One workspace',
      advancedEyebrow: 'Advanced tools',
      workflowEyebrow: 'A clear workflow',
      pricingEyebrow: 'Simple pricing',
      pricingTitle: 'Start free and scale when needed',
      pricingSubtitle: 'Prices and usage stay synced automatically — no forced renewal, no hidden fees.',
      priceLabel: 'Price',
      currencyLabel: 'EGP',
      unavailablePrice: '—',
      pricingPlans: [
        {
          eyebrow: 'Explore', name: 'Free plan', chip: 'Start here',
          description: 'Try the complete workflow and create your first campaigns with no commitment.',
          priceNote: 'Always free to start', creditsLabel: '{count} free campaigns',
          featuresTitle: 'What you get',
          features: ['Save and review campaigns', 'Budget simulator', 'Launch package', 'Editable ad copy'],
          cta: 'Start free', note: 'No credit card required', popularLabel: '',
        },
        {
          eyebrow: 'For marketers and businesses', name: 'Pro', chip: '',
          description: 'Flexible credits and advanced tools for professional, measurable campaigns.',
          priceNote: 'One-time payment, no auto-renewal', creditsLabel: '{count} AI operations',
          featuresTitle: 'Everything in Free, plus',
          features: ['Advanced rewriting with flexible credits', 'Full analytics and simulation', 'Meta policy checker', 'Real-data calibration', 'Client review portal'],
          cta: 'Choose Pro', note: 'Best value for individual use', popularLabel: 'Most popular',
        },
        {
          eyebrow: 'For teams and agencies', name: 'Agency', chip: 'For teams',
          description: 'One workspace to manage teams, clients, and approvals at scale.',
          priceNote: 'One-time payment with full credit control', creditsLabel: '{count} managed operations',
          featuresTitle: 'Everything in Pro, plus',
          features: ['Team workspace and approvals', 'Per-member credit tracking', 'A/B test matrix', 'Expanded, manageable Agency credits', 'Priority support'],
          cta: 'Start as agency', note: 'Built for multiple members and clients', popularLabel: '',
        },
      ],
      pricingFootnote: 'Start free and move to any plan later without losing your saved campaigns.',
      finalEyebrow: 'Your next step',
      finalPanelTitle: 'Ready in minutes',
      finalHighlights: ['Meta policy checks', 'No credit card', '3 free campaigns', 'Instant output'],
      footerCtaTitle: 'Plan clearly. Launch confidently.',
      footerCtaSubtitle: 'One workspace to turn an offer brief into a reviewable, measurable campaign.',
      footerCtaButton: 'Start free',
      footerTagline: 'Arabic campaign intelligence',
      footerAssurance: 'Reviewable output before launch',
      footerPlatformTitle: 'Platform',
      footerPlatformLinks: ['Home', 'Features', 'Advanced tools', 'Pricing', 'Sign up', 'Sign in'],
      footerCompanyTitle: 'Company',
      footerCompanyLinks: ['About us', 'FAQ', 'Contact us', 'Privacy policy', 'Terms of use'],
      footerContactTitle: 'Get in touch',
      footerContactDescription: 'Our team is ready to help you choose the right path for your campaign.',
      footerWhatsappLabel: 'Chat on WhatsApp',
      footerLegalLinks: ['Privacy', 'Terms', 'Support'],
      footerCopyright: '© {year} Istihdaf. All rights reserved.',
      footerMade: 'Built with Egyptian expertise',
    },
  },
};

export const DEFAULT_LEGAL_CONTENT: LegalContent = {
  ar: {
    eyebrow: 'المعلومات القانونية',
    title: 'سياسة الخصوصية والشروط والأحكام',
    intro: 'نوضح هنا كيفية جمع بياناتك واستخدامها وحمايتها، بالإضافة إلى الشروط والأحكام التي تنظم استخدامك لمنصة استهداف وخدماتها.',
    updatedAt: 'آخر تحديث: أغسطس 2026',
    privacyHeading: 'أولا: سياسة الخصوصية',
    privacySections: [
      { heading: 'البيانات التي قد نجمعها', body: 'نحن نقدر خصوصيتك ونلتزم بالتعامل مع بياناتك بمسؤولية وشفافية. توضح سياسة الخصوصية هذه أنواع البيانات التي قد نجمعها عند استخدامك لمنصة استهداف، وكيف نستخدمها ونحميها، والخيارات والحقوق المتاحة لك فيما يتعلق ببياناتك.', points: [
        'بيانات الحساب، مثل الاسم وعنوان البريد الإلكتروني وبيانات تسجيل الدخول اللازمة لإدارة حسابك.',
        'بيانات نشاطك أو مشروعك التي تدخلها في المنصة، مثل نوع النشاط والمجال والجمهور المستهدف وتفاصيل العرض، وذلك لتقديم خدمات التحليل وإنشاء استراتيجيات الاستهداف والمحتوى الإعلاني.',
        'بيانات الاستخدام والتفاعل مع المنصة، مثل الصفحات أو الميزات التي تستخدمها، بهدف تحسين أداء المنصة وتطوير خدماتها.',
        'بيانات مرتبطة بالاشتراكات والمدفوعات، عند الاشتراك في إحدى الباقات المدفوعة، وفقا لطريقة الدفع ومزود خدمة الدفع المستخدم.',
      ] },
      { heading: 'كيف نستخدم بياناتك', points: [
        'تشغيل المنصة وتقديم خدماتها والميزات المتاحة ضمن باقتك.',
        'تحليل بيانات المشروع التي تدخلها لإنشاء استراتيجيات الاستهداف والمحتوى الإعلاني المطلوب.',
        'تحسين أداء المنصة وتطوير الميزات والخدمات وتجربة المستخدم.',
        'التواصل معك بشأن حسابك أو اشتراكك أو التحديثات المهمة أو الدعم الفني.',
        'إدارة الاشتراكات والمدفوعات ومعالجة الطلبات المتعلقة بحسابك.',
        'الحفاظ على أمن المنصة ومنع إساءة الاستخدام أو الأنشطة غير المصرح بها.',
      ] },
      { heading: 'ملفات تعريف الارتباط وتقنيات التتبع', body: 'قد تستخدم المنصة ملفات تعريف الارتباط وتقنيات مشابهة لتشغيل بعض الوظائف الأساسية، وحفظ تفضيلاتك مثل اللغة المختارة، وتحسين تجربة الاستخدام. وقد نستخدم أدوات تحليلية، عند تفعيلها، لفهم كيفية استخدام المنصة وتحسين أدائها. يمكنك التحكم في ملفات تعريف الارتباط من خلال إعدادات متصفحك، مع العلم أن تعطيل بعض الملفات قد يؤثر في بعض وظائف الموقع.' },
      { heading: 'مشاركة البيانات مع الجهات الأخرى', body: 'لا نبيع بياناتك الشخصية أو نؤجرها لأطراف أخرى. قد تتم مشاركة بعض البيانات مع مزودي الخدمات الذين يساعدوننا في تشغيل المنصة، مثل خدمات الاستضافة أو معالجة المدفوعات أو الخدمات التقنية، وذلك بالقدر اللازم لتقديم الخدمة وتشغيلها. وقد نشارك البيانات عندما يكون ذلك مطلوبا بموجب القانون أو لحماية حقوق المنصة ومستخدميها.' },
      { heading: 'حقوقك المتعلقة ببياناتك', body: 'لممارسة أي من هذه الحقوق أو للاستفسار عن كيفية التعامل مع بياناتك، يمكنك التواصل معنا عبر البريد الإلكتروني info@istihdaf.site.', points: [
        'طلب الوصول إلى بياناتك الشخصية والاطلاع عليها.',
        'طلب تصحيح البيانات غير الدقيقة أو غير المكتملة.',
        'طلب حذف حسابك وبياناتك، وفقا للمتطلبات والالتزامات القانونية المعمول بها.',
        'طلب الحصول على نسخة من بياناتك أو تصديرها، عندما يكون ذلك متاحا ومناسبا تقنيا.',
      ] },
      { heading: 'أمان البيانات', body: 'نتخذ إجراءات تقنية وتنظيمية مناسبة تهدف إلى حماية البيانات من الوصول غير المصرح به أو الفقد أو التغيير أو سوء الاستخدام. ومع ذلك، لا يمكن ضمان أن تكون أي وسيلة إلكترونية لنقل البيانات أو تخزينها آمنة بشكل مطلق، ولذلك لا يمكننا ضمان الحماية الكاملة من جميع المخاطر الأمنية المحتملة.' },
    ],
    termsHeading: 'ثانيا: الشروط والأحكام',
    termsSections: [
      { heading: 'مقدمة', body: 'باستخدامك لمنصة استهداف أو أي من خدماتها، فإنك تقر بأنك قرأت هذه الشروط والأحكام ووافقت على الالتزام بها. إذا كنت لا توافق على أي من هذه الشروط، يرجى التوقف عن استخدام المنصة.' },
      { heading: 'استخدام المنصة والحسابات', points: [
        'يجب استخدام المنصة لأغراض قانونية ومشروعة وبما يتوافق مع القوانين واللوائح المعمول بها.',
        'أنت مسؤول عن دقة البيانات والمعلومات التي تدخلها في المنصة، وعن مراجعة النتائج قبل استخدامها أو نشرها.',
        'أنت مسؤول عن الحفاظ على سرية بيانات تسجيل الدخول الخاصة بحسابك وعن جميع الأنشطة التي تتم من خلاله.',
        'لا يجوز استخدام المنصة لإنشاء محتوى غير قانوني أو مضلل أو ينتهك حقوق الآخرين أو يخالف سياسات منصات الإعلانات.',
      ] },
      { heading: 'الباقات والاشتراكات', points: [
        'تتيح الباقة المجانية استخداما محدودا للمنصة وفقا للمميزات والحدود الموضحة في صفحة الأسعار.',
        'تتوفر الباقات المدفوعة، بما في ذلك Pro وAgency، وفقا للمميزات والأسعار الموضحة في صفحة الأسعار.',
        'قد تختلف المميزات والحدود المتاحة لكل باقة، ويحق للمنصة تحديثها أو تعديلها عند الحاجة.',
      ] },
      { heading: 'المحتوى والنتائج التي يتم إنشاؤها بواسطة الذكاء الاصطناعي', body: 'تعتمد استهداف على تقنيات الذكاء الاصطناعي لإنشاء مقترحات لاستراتيجيات الاستهداف والمحتوى الإعلاني. تمثل هذه النتائج أدوات مساعدة لاتخاذ القرار وليست بديلا عن الخبرة البشرية أو المراجعة المتخصصة. يجب عليك مراجعة جميع النتائج والتأكد من دقتها وملاءمتها لمشروعك قبل استخدامها أو نشرها.' },
      { heading: 'إخلاء المسؤولية وحدود المسؤولية', body: 'لا تضمن استهداف تحقيق نتائج إعلانية أو تسويقية محددة، بما في ذلك عدد العملاء المحتملين أو تكلفة الليد أو معدل التحويل. تعتمد نتائج الحملات على عوامل متعددة، منها الميزانية وجودة المحتوى والإبداع الإعلاني وقوة العرض والمنافسة والجمهور وأداء منصات الإعلانات الخارجية. إلى أقصى حد يسمح به القانون المعمول به، لا تتحمل استهداف أو فريقها المسؤولية عن أي خسائر مباشرة أو غير مباشرة ناتجة عن الاعتماد على النتائج التي يتم إنشاؤها بواسطة المنصة دون مراجعة مناسبة، أو عن التغييرات أو الأعطال التي تطرأ على خدمات أو سياسات الجهات الخارجية، بما في ذلك منصات الإعلانات.' },
      { heading: 'الإلغاء والاسترجاع', points: [
        'يمكنك طلب إلغاء اشتراكك المدفوع في أي وقت، ويستمر الوصول إلى المميزات المدفوعة حتى نهاية الفترة التي تم دفع قيمتها، ما لم تنص شروط الاشتراك على خلاف ذلك.',
        'لا يتم رد المبالغ المدفوعة عن الفترة التي بدأ استخدامها، إلا في الحالات التي يتم قبولها وفقا لسياسة الاسترجاع المعمول بها أو وفقا لما يقتضيه القانون.',
        'إذا واجهت مشكلة تتعلق بالدفع أو الاشتراك، يمكنك التواصل مع فريق الدعم لمراجعة الحالة.',
      ] },
      { heading: 'التعديلات على سياسة الخصوصية والشروط', body: 'قد نقوم بتحديث سياسة الخصوصية أو هذه الشروط من وقت لآخر لتعكس التغييرات في خدماتنا أو المتطلبات القانونية أو التشغيلية. عند إجراء تغييرات جوهرية، سنعمل على توضيح ذلك من خلال المنصة أو وسائل التواصل المناسبة. ويعد استمرارك في استخدام المنصة بعد سريان التعديلات موافقة على النسخة المحدثة.' },
    ],
    contactNote: 'إذا كان لديك أي استفسار يتعلق بسياسة الخصوصية أو الشروط والأحكام أو طريقة التعامل مع بياناتك، يمكنك التواصل مع فريق استهداف عبر البريد الإلكتروني: info@istihdaf.site.',
  },
  en: {
    eyebrow: 'Legal Information',
    title: 'Privacy Policy & Terms of Use',
    intro: 'This page explains how we collect, use, and protect your data, along with the terms that govern your use of the Istihdaf platform and its services.',
    updatedAt: 'Last updated: August 2026',
    privacyHeading: 'Part One: Privacy Policy',
    privacySections: [
      { heading: 'Information We May Collect', body: 'We value your privacy and are committed to handling your data responsibly and transparently. This Privacy Policy explains what information we may collect when you use Istihdaf, how we use and protect it, and the choices and rights available to you regarding your data.', points: [
        'Account information, such as your name, email address, and login details required to manage your account.',
        'Business or project information you provide, such as your industry, activity type, target audience, and offer details, in order to provide analysis and generate targeting strategies and advertising content.',
        'Usage and interaction data, such as the pages or features you use, to improve platform performance and develop our services.',
        'Subscription and payment-related information when you subscribe to a paid plan, depending on the payment method and payment provider used.',
      ] },
      { heading: 'How We Use Your Data', points: [
        'To operate the platform and provide its services and features available under your plan.',
        'To process the project information you provide in order to generate the requested targeting strategies and advertising content.',
        'To improve platform performance, develop features and services, and enhance the user experience.',
        'To communicate with you about your account, subscription, important updates, or technical support.',
        'To manage subscriptions and payments and process requests related to your account.',
        'To maintain platform security and prevent misuse or unauthorized activities.',
      ] },
      { heading: 'Cookies and Tracking Technologies', body: 'The platform may use cookies and similar technologies to support essential functionality, remember preferences such as your selected language, and improve your experience. We may also use analytics tools, when enabled, to understand platform usage and improve performance. You can manage cookies through your browser settings, although disabling certain cookies may affect some website functionality.' },
      { heading: 'Sharing Your Information', body: 'We do not sell or rent your personal information to third parties. Certain information may be shared with service providers that help us operate the platform, such as hosting, payment processing, or technical service providers, only as necessary to provide and operate the service. We may also disclose information when required by law or when necessary to protect the rights of the platform and its users.' },
      { heading: 'Your Data Rights', body: 'To exercise any of these rights or ask questions about how your data is handled, you can contact us at info@istihdaf.site.', points: [
        'Request access to and review your personal information.',
        'Request correction of inaccurate or incomplete information.',
        'Request deletion of your account and data, subject to applicable legal requirements and obligations.',
        'Request a copy or export of your data where technically available and applicable.',
      ] },
      { heading: 'Data Security', body: 'We take appropriate technical and organizational measures designed to protect data against unauthorized access, loss, alteration, or misuse. However, no electronic method of transmission or storage can be guaranteed to be completely secure, and therefore we cannot guarantee protection against every potential security risk.' },
    ],
    termsHeading: 'Part Two: Terms of Use',
    termsSections: [
      { heading: 'Introduction', body: 'By accessing or using Istihdaf or any of its services, you acknowledge that you have read and agree to these Terms of Use. If you do not agree with any part of these terms, please discontinue use of the platform.' },
      { heading: 'Platform and Account Use', points: [
        'The platform must be used only for lawful and legitimate purposes and in compliance with applicable laws and regulations.',
        'You are responsible for the accuracy of the information you provide and for reviewing generated outputs before using or publishing them.',
        'You are responsible for maintaining the confidentiality of your login credentials and for activities conducted through your account.',
        'You may not use the platform to create unlawful or misleading content, infringe the rights of others, or violate advertising platform policies.',
      ] },
      { heading: 'Plans and Subscriptions', points: [
        'The free plan provides limited access to the platform according to the features and limits described on the Pricing page.',
        'Paid plans, including Pro and Agency, are available according to the features and prices described on the Pricing page.',
        'Features and usage limits may vary by plan, and the platform may update or modify them when necessary.',
      ] },
      { heading: 'AI-Generated Content and Outputs', body: 'Istihdaf uses artificial intelligence technologies to generate targeting strategy suggestions and advertising content. These outputs are intended as decision-support tools and are not a substitute for human expertise or professional review. You are responsible for reviewing all outputs and verifying their accuracy and suitability for your project before using or publishing them.' },
      { heading: 'Disclaimer and Limitation of Liability', body: 'Istihdaf does not guarantee specific advertising or marketing results, including lead volume, lead cost, or conversion rates. Campaign performance depends on multiple factors, including budget, content quality, creative quality, offer strength, competition, audience, and the performance of third-party advertising platforms. To the maximum extent permitted by applicable law, Istihdaf and its team shall not be liable for direct or indirect losses resulting from reliance on platform-generated outputs without appropriate review, or from changes to or interruptions in third-party services or policies, including advertising platforms.' },
      { heading: 'Cancellation and Refunds', points: [
        'You may request cancellation of your paid subscription at any time. Access to paid features will generally remain available until the end of the paid billing period, unless otherwise stated in the applicable subscription terms.',
        'Payments for a billing period that has already begun are generally non-refundable, except where a refund is approved under the applicable refund policy or required by law.',
        'If you experience a payment or subscription issue, you can contact our support team to review the situation.',
      ] },
      { heading: 'Changes to This Privacy Policy and These Terms', body: 'We may update this Privacy Policy or these Terms from time to time to reflect changes to our services or legal and operational requirements. When material changes are made, we will take reasonable steps to communicate them through the platform or other appropriate channels. Your continued use of the platform after the updated terms take effect constitutes acceptance of the revised version.' },
    ],
    contactNote: 'If you have any questions about this Privacy Policy, these Terms of Use, or how your data is handled, you can contact the Istihdaf team at: info@istihdaf.site.',
  },
};

export const DEFAULT_CONTACT_PAGE_CONTENT: ContactPageContent = {
  ar: {
    eyebrow: 'تواصل معنا',
    title: 'هل لديك سؤال؟ نحن هنا لمساعدتك',
    subtitle: "يسعد فريق استهداف بمساعدتك سواء كان لديك استفسار حول المنصة أو تحتاج إلى دعم فني أو ترغب في معرفة المزيد عن الباقات والخدمات، تواصل معنا وسنحرص على الرد عليك في أقرب وقت ممكن.",
    formEyebrow: 'أرسل لنا رسالة',
    formTitle: 'لنتحدث عن احتياجاتك',
    formSubtitle: 'املأ النموذج وسنتواصل معك في أقرب وقت.',
    inquiryTypes: ['استفسار عام', 'دعم فني', 'المبيعات والباقات', 'باقة Agency'],
    detailsEyebrow: 'طرق التواصل',
    detailsTitle: 'تواصل معنا بالطريقة التي تناسبك',
    detailsSubtitle: 'فريق استهداف جاهز للإجابة عن استفساراتك ومساعدتك في الحصول على أفضل تجربة ممكنة.',
    phone: '01270257375',
    whatsapp: '201270257375',
    email: 'info@istihdaf.site',
    address: 'مدينة نصر، القاهرة، مصر',
    successMessage: 'تم استلام رسالتك بنجاح. سنتواصل معك في أقرب وقت ممكن.',
    errorMessage: 'حدثت مشكلة أثناء إرسال رسالتك. يرجى المحاولة مرة أخرى أو التواصل معنا مباشرة عبر البريد الإلكتروني.',
  },
  en: {
    eyebrow: 'Contact Us',
    title: "Have a question? We're here to help",
    subtitle: "The Istihdaf team is here to help. Whether you have a question about the platform, need technical support, or want to learn more about our plans and services, get in touch and we'll get back to you as soon as possible.",
    formEyebrow: 'Send Us a Message',
    formTitle: "Let's Talk About Your Needs",
    formSubtitle: "Fill out the form and we'll get back to you shortly.",
    inquiryTypes: ['General Inquiry', 'Technical Support', 'Sales & Plans', 'Agency Plan'],
    detailsEyebrow: 'Contact Options',
    detailsTitle: 'Choose the Way That Works for You',
    detailsSubtitle: 'The Istihdaf team is ready to answer your questions and help you get the best experience possible.',
    phone: '01270257375',
    whatsapp: '201270257375',
    email: 'info@istihdaf.site',
    address: 'Nasr City, Cairo, Egypt',
    successMessage: "Your message has been received successfully. We'll get back to you shortly.",
    errorMessage: 'Something went wrong while sending your message. Please try again or contact us directly by email.',
  },
};

export const DEFAULT_FAQ_CONTENT: FaqContent = {
  ar: {
    eyebrow: 'الأسئلة الشائعة',
    title: 'كل ما تحتاج إلى معرفته عن استهداف',
    subtitle: 'تعرف على طريقة عمل استهداف، وكيف يمكن أن يساعدك في إعداد استراتيجيات التسويق وإنشاء المحتوى الإعلاني بطريقة أكثر سرعة وتنظيما.',
    groups: [
      {
        eyebrow: 'عن استهداف',
        heading: 'ما هي استهداف وكيف تعمل؟',
        items: [
          { question: 'ما هي منصة استهداف؟', answer: 'استهداف منصة ذكاء اصطناعي تساعد المسوقين وMedia Buyers وفرق التسويق والوكالات على تحويل فكرة الحملة إلى استراتيجية استهداف وجمهور ومحتوى إعلاني جاهز، في مسار عمل واحد بدلا من التنقل بين أدوات منفصلة.' },
          { question: 'لمن تم تصميم المنصة؟', answer: 'استهداف مصممة للمسوقين وMedia Buyers وفرق التسويق وصنّاع المحتوى والوكالات، سواء كانوا يديرون حملة واحدة أو حملات لعدة عملاء في مجالات مختلفة.' },
          { question: 'هل استهداف مخصصة للعقارات فقط؟', answer: 'لا. تدعم استهداف مجالات متعددة مثل العقارات والتجارة الإلكترونية والخدمات والتعليم وغيرها، وتتكيف الأسئلة والمخرجات مع طبيعة كل نشاط وهدف الحملة.' },
        ],
      },
      {
        eyebrow: 'استخدام المنصة',
        heading: 'ماذا يمكنني إنشاء باستخدام استهداف؟',
        items: [
          { question: 'ماذا يمكنني إنشاء باستخدام استهداف؟', answer: 'يمكنك إنشاء استراتيجية استهداف، وتحديد الجمهور المناسب، ونسخ إعلانية متعددة، وHooks وHeadlines، وأفكار محتوى وسيناريوهات فيديو، وحزمة إطلاق كاملة للحملة.' },
          { question: 'ما المنصات الإعلانية التي تدعمها؟', answer: 'تساعدك استهداف على إنشاء محتوى واستراتيجيات مناسبة لمنصات مثل Meta وTikTok وGoogle Ads وSnapchat وLinkedIn، بحسب طبيعة كل منصة وجمهورها.' },
          { question: 'هل تدعم العربية والإنجليزية؟', answer: 'نعم، تدعم المنصة اللغتين العربية والإنجليزية في الواجهة والمحتوى الإعلاني الذي يتم إنشاؤه.' },
          { question: 'هل النتائج جاهزة للاستخدام مباشرة؟', answer: 'يتم إنشاء المحتوى ليكون قريبا من الجاهزية للاستخدام والنشر، مع صياغة العناوين والنصوص والرسائل التسويقية بما يتناسب مع نشاطك وجمهورك المستهدف. مع ذلك ننصح دائما بمراجعة النتائج قبل النشر.' },
        ],
      },
      {
        eyebrow: 'الحساب والباقات',
        heading: 'كيف يعمل الاشتراك والرصيد؟',
        items: [
          { question: 'هل توجد نسخة مجانية؟', answer: 'نعم، تتيح الباقة المجانية عددا محدودا من الحملات للاستكشاف، ويمكنك الاطلاع على تفاصيل الباقات والأسعار والمميزات المتاحة لكل باقة من خلال صفحة الأسعار.' },
          { question: 'ماذا يحدث عند انتهاء الباقة؟', answer: 'يمكنك تجديد اشتراكك أو الترقية إلى باقة أعلى في أي وقت من خلال حسابك، وتبقى بياناتك وحملاتك المحفوظة متاحة.' },
          { question: 'هل الاشتراك يتجدد تلقائيا؟', answer: 'تعتمد آلية التجديد على طريقة الدفع المستخدمة، ويمكنك مراجعة أو إلغاء التجديد التلقائي من إعدادات حسابك في أي وقت.' },
          { question: 'كيف تعمل الـ Credits؟', answer: 'يستهلك كل إجراء يعتمد على الذكاء الاصطناعي، مثل إنشاء استراتيجية أو نسخة إعلانية، جزءا من رصيد Credits الخاص بباقتك، ويمكنك متابعة الاستهلاك من حسابك.' },
        ],
      },
      {
        eyebrow: 'الذكاء الاصطناعي والبيانات',
        heading: 'كيف يتم التعامل مع بياناتي ومخرجات الذكاء الاصطناعي؟',
        items: [
          { question: 'هل تعتمد النتائج بالكامل على الذكاء الاصطناعي؟', answer: 'تعتمد استهداف على الذكاء الاصطناعي لإنشاء مقترحات الاستراتيجية والمحتوى الإعلاني، وهي أدوات مساعدة لاتخاذ القرار وليست بديلا عن الخبرة البشرية.' },
          { question: 'هل يجب مراجعة المحتوى قبل النشر؟', answer: 'نعم، ننصح دائما بمراجعة النتائج والتأكد من دقتها وملاءمتها لنشاطك وجمهورك قبل استخدامها أو نشرها.' },
          { question: 'كيف يتم التعامل مع بيانات المستخدم؟', answer: 'يتم التعامل مع بياناتك وفقا لسياسة الخصوصية المعتمدة في المنصة، ويمكنك الاطلاع على التفاصيل الكاملة من صفحة سياسة الخصوصية، أو طلب حذف حسابك وبياناتك من خلال التواصل مع فريق الدعم عبر info@istihdaf.site.' },
        ],
      },
    ],
    ctaTitle: 'هل لديك سؤال آخر؟',
    ctaSubtitle: 'إذا لم تجد إجابة سؤالك هنا، تواصل مع فريقنا وسنساعدك في الحصول على المعلومات التي تحتاج إليها.',
    ctaButton: 'تواصل معنا',
  },
  en: {
    eyebrow: 'FAQ',
    title: 'Everything You Need to Know About Istihdaf',
    subtitle: 'Learn how Istihdaf works and how it can help you build marketing strategies and ad copy faster and in a more organized way.',
    groups: [
      {
        eyebrow: 'About Istihdaf',
        heading: 'What Is Istihdaf and How Does It Work?',
        items: [
          { question: 'What is Istihdaf?', answer: 'Istihdaf is an AI platform that helps marketers, media buyers, marketing teams, and agencies turn a campaign idea into a targeting strategy, audience, and ready ad copy, in one workflow instead of scattered tools.' },
          { question: 'Who is the platform designed for?', answer: 'Istihdaf is built for marketers, media buyers, marketing teams, content creators, and agencies, whether managing a single campaign or campaigns for multiple clients across different industries.' },
          { question: 'Is Istihdaf only for real estate?', answer: 'No. Istihdaf supports multiple industries including real estate, e-commerce, services, education, and more, with questions and outputs that adapt to the business and campaign goal.' },
        ],
      },
      {
        eyebrow: 'Using the Platform',
        heading: 'What Can I Create With Istihdaf?',
        items: [
          { question: 'What can I create with Istihdaf?', answer: 'You can create a targeting strategy, define the right audience, generate multiple ad copies, hooks and headlines, content ideas and video scripts, and a complete campaign launch package.' },
          { question: 'Which advertising platforms does it support?', answer: 'Istihdaf helps you create content and strategies suited to platforms such as Meta, TikTok, Google Ads, Snapchat, and LinkedIn, based on each platform and its audience.' },
          { question: 'Does it support Arabic and English?', answer: 'Yes, the platform supports both Arabic and English across the interface and the ad content it generates.' },
          { question: 'Is the output ready to use right away?', answer: 'Generated content is close to publish-ready, with headlines, copy, and messaging tailored to your business and target audience. We still recommend reviewing every output before publishing.' },
        ],
      },
      {
        eyebrow: 'Account & Plans',
        heading: 'How Do Subscriptions and Credits Work?',
        items: [
          { question: 'Is there a free plan?', answer: 'Yes, the free plan includes a limited number of campaigns to explore the platform. You can review full plan details, pricing, and features on the Pricing page.' },
          { question: 'What happens when my plan runs out?', answer: 'You can renew or upgrade to a higher plan at any time from your account, and your saved data and campaigns remain accessible.' },
          { question: 'Does my subscription renew automatically?', answer: 'Renewal depends on the payment method used, and you can review or cancel auto-renewal from your account settings at any time.' },
          { question: 'How do credits work?', answer: 'Every AI-powered action, such as generating a strategy or an ad copy, uses part of your plan\'s credit balance, which you can track from your account.' },
        ],
      },
      {
        eyebrow: 'AI & Data',
        heading: 'How Is My Data and AI Output Handled?',
        items: [
          { question: 'Do the results rely entirely on AI?', answer: 'Istihdaf uses AI to generate strategy and ad content suggestions. These are decision-support tools, not a substitute for human expertise.' },
          { question: 'Should I review content before publishing?', answer: 'Yes, we always recommend reviewing every output and confirming it is accurate and suitable for your business and audience before using or publishing it.' },
          { question: 'How is my data handled?', answer: 'Your data is handled according to the platform\'s privacy policy. You can review the full details on the Privacy Policy page, or request deletion of your account and data by contacting our support team at info@istihdaf.site.' },
        ],
      },
    ],
    ctaTitle: 'Still Have a Question?',
    ctaSubtitle: 'If you could not find the answer you are looking for, contact our team and we will help you get the information you need.',
    ctaButton: 'Contact Us',
  },
};

export const DEFAULT_ABOUT_CONTENT: AboutContent = {
  ar: {
    eyebrow: 'من نحن',
    title: 'خبرة حقيقية داخل الحملات، تحولت إلى منصة ذكاء اصطناعي',
    intro: 'استهداف منصة ذكاء اصطناعي تساعد المسوقين وصنّاع المحتوى والوكالات على الانتقال من فكرة الحملة إلى استراتيجية استهداف ومحتوى إعلاني جاهز، عبر مجالات متعددة أهمها العقارات والتجارة الإلكترونية والخدمات والتعليم.',
    storyEyebrow: 'قصتنا',
    storyTitle: 'لماذا أنشأنا استهداف؟',
    storyParagraphs: [
      'يستخدم المسوق عادة عدة أدوات منفصلة في كل حملة: أداة للبحث، أخرى للكتابة، وثالثة للمتابعة، وينتقل بينها ويبدأ من جديد في كل مرة، فتتحول عملية إعداد الحملة إلى وقت وجهد أكبر مما تستحق.',
      'استهداف بدأت من تجربة حقيقية داخل الحملات، وليس من فكرة تقنية منفصلة عن السوق. جمعنا مراحل العمل، من الفكرة إلى الاستراتيجية والجمهور والمحتوى الإعلاني والتنفيذ، في مسار واحد واضح يساعد فرق التسويق على العمل بكفاءة أكبر.',
    ],
    teamTitle: 'تطوير فريق تروفا ميديا',
    teamBody: 'تم تطوير استهداف بواسطة فريق تروفا ميديا المتخصص في التسويق الرقمي واستراتيجيات الإعلانات، بهدف تقديم أدوات ذكية تعتمد على الذكاء الاصطناعي لمساعدة المسوقين وصنّاع المحتوى والوكالات على بناء حملات أكثر دقة وكفاءة.',
    visionEyebrow: 'رؤيتنا ورسالتنا',
    visionTitle: 'نهدف إلى تبسيط الطريق من الفكرة إلى الحملة',
    visionHeading: 'رؤيتنا',
    visionBody: 'أن تصبح استهداف مساحة العمل المفضلة لفرق التسويق في الأسواق العربية، ونقطة البداية لبناء أي حملة إعلانية بدقة ووضوح، مهما كان مجال النشاط.',
    missionHeading: 'رسالتنا',
    missionBody: 'تمكين المسوقين والوكالات من تقليل الوقت والجهد المبذولين في إعداد الحملات، وتحويل الخبرة التسويقية إلى أدوات ذكية تساعدهم على العمل بكفاءة واتخاذ قرارات أفضل.',
    valuesEyebrow: 'قيمنا',
    valuesTitle: 'المبادئ التي نبني عليها استهداف',
    values: [
      { title: 'الدقة', body: 'نساعدك على بناء استراتيجيات استهداف أكثر دقة اعتمادا على طبيعة النشاط والجمهور والسوق المستهدف.' },
      { title: 'السرعة', body: 'اختصر الوقت الذي تحتاجه لإعداد حملتك وانتقل من الفكرة إلى استراتيجية ومحتوى إعلاني جاهز في دقائق.' },
      { title: 'فهم السوق', body: 'نركز على طبيعة الأسواق العربية ونقدم محتوى واستراتيجيات تتناسب مع الجمهور المحلي وسلوك العملاء.' },
    ],
    stats: [
      { value: '+3', label: 'حملات مجانية للبدء' },
      { value: '+10', label: 'مجالات نشاط مدعومة' },
      { value: 'AR/EN', label: 'محتوى عربي وإنجليزي' },
      { value: 'دقائق', label: 'من الفكرة إلى الحملة' },
    ],
    ctaTitle: 'ابدأ بناء حملتك التالية بذكاء',
    ctaSubtitle: 'جرب استهداف واكتشف كيف يمكن للذكاء الاصطناعي أن يساعدك على إعداد استراتيجية استهداف ومحتوى إعلاني احترافي في وقت أقل.',
    ctaButton: 'ابدأ تجربتك مجانا',
  },
  en: {
    eyebrow: 'About Us',
    title: 'Real campaign experience, turned into an AI platform',
    intro: 'Istihdaf is an AI platform that helps marketers, content creators, and agencies move from a campaign idea to a full targeting strategy and ready ad copy — across industries including real estate, e-commerce, services, and education.',
    storyEyebrow: 'Our Story',
    storyTitle: 'Why We Built Istihdaf',
    storyParagraphs: [
      'Marketers usually juggle several separate tools for a single campaign: one for research, another for writing, another for tracking. Switching between them and starting over each time turns campaign prep into far more effort than it should take.',
      'Istihdaf started from real experience inside campaigns, not from a technical idea disconnected from the market. We brought every stage of the work, from the idea to strategy, audience, ad content, and execution, into one clear workflow that helps marketing teams work more efficiently.',
    ],
    teamTitle: 'Developed by Truva Media',
    teamBody: 'Istihdaf was developed by the Truva Media team, specialized in digital marketing and advertising strategy, with the goal of building AI-powered tools that help marketers, content creators, and agencies run more precise and efficient campaigns.',
    visionEyebrow: 'Vision & Mission',
    visionTitle: 'Our Goal Is to Simplify the Path From Idea to Campaign',
    visionHeading: 'Our Vision',
    visionBody: 'To become the preferred workspace for marketing teams across Arabic-speaking markets, and the starting point for building any advertising campaign with precision and clarity, regardless of industry.',
    missionHeading: 'Our Mission',
    missionBody: 'To help marketers and agencies reduce the time and effort spent preparing campaigns, turning marketing expertise into intelligent tools that enable greater efficiency and better decisions.',
    valuesEyebrow: 'Our Values',
    valuesTitle: 'The Principles Behind Istihdaf',
    values: [
      { title: 'Precision', body: 'We help you build more precise targeting strategies based on your business, audience, and target market.' },
      { title: 'Speed', body: 'Reduce the time needed to prepare your campaign and move from idea to a complete strategy and ready-to-use ad copy in minutes.' },
      { title: 'Market Understanding', body: 'We focus on Arabic-speaking markets and create strategies and content tailored to local audiences and customer behavior.' },
    ],
    stats: [
      { value: '3+', label: 'Free campaigns to start' },
      { value: '10+', label: 'Industries supported' },
      { value: 'AR/EN', label: 'Arabic & English content' },
      { value: 'Minutes', label: 'From idea to campaign' },
    ],
    ctaTitle: 'Build Your Next Campaign Smarter',
    ctaSubtitle: 'Try Istihdaf and discover how AI can help you create a professional targeting strategy and ad copy in less time.',
    ctaButton: 'Start Your Free Trial',
  },
};

// إعدادات السيو الافتراضية — كل الحقول فاضية عمدًا (السلوك الافتراضي إن كل صفحة
// تبني عنوانها/وصفها من محتواها العادي زي ما كانت بتعمل قبل لوحة السيو؛ أي حقل
// يملأه الأدمن هنا بيبقى أولوية ويطغى على الافتراضي).
const EMPTY_SEO_LOCALE: SeoLocaleEntry = { title: '', description: '' };
function emptySeoPage(): SeoPageSettings {
  return { ar: { ...EMPTY_SEO_LOCALE }, en: { ...EMPTY_SEO_LOCALE } };
}
export const DEFAULT_SEO_SETTINGS: SeoSettings = {
  home: emptySeoPage(),
  about: emptySeoPage(),
  faq: emptySeoPage(),
  contact: emptySeoPage(),
  privacy: emptySeoPage(),
  terms: emptySeoPage(),
};

const DEFAULT_SETTINGS: SiteSettings = {
  primaryColor: '#0A2038',
  accentColor: '#D6AE6C',
  fontFamily: "'IBM Plex Sans Arabic', 'Inter', ui-sans-serif, system-ui, sans-serif",
  marketingContent: DEFAULT_MARKETING_CONTENT,
  legalContent: DEFAULT_LEGAL_CONTENT,
  contactPageContent: DEFAULT_CONTACT_PAGE_CONTENT,
  faqContent: DEFAULT_FAQ_CONTENT,
  aboutContent: DEFAULT_ABOUT_CONTENT,
  seoSettings: DEFAULT_SEO_SETTINGS,
};

function mergeLocaleArray<T>(defaults: T[], incoming: unknown): T[] {
  if (!Array.isArray(incoming) || incoming.length === 0) return defaults;
  return incoming.slice(0, Math.max(defaults.length, incoming.length)).map((item, index) => ({
    ...(defaults[index] || defaults[defaults.length - 1]),
    ...(typeof item === 'object' && item ? item : {}),
  }));
}

function mergeStringArray(defaults: string[], incoming: unknown): string[] {
  if (!Array.isArray(incoming) || incoming.length === 0) return defaults;
  return defaults.map((fallback, index) => typeof incoming[index] === 'string' ? incoming[index] : fallback);
}

function mergeMarketingUi(defaults: MarketingUiLocaleContent, incoming: Partial<MarketingUiLocaleContent> | undefined): MarketingUiLocaleContent {
  return {
    ...defaults,
    ...(incoming || {}),
    navLabels: mergeStringArray(defaults.navLabels, incoming?.navLabels),
    heroProof: mergeStringArray(defaults.heroProof, incoming?.heroProof),
    finalHighlights: mergeStringArray(defaults.finalHighlights, incoming?.finalHighlights),
    footerPlatformLinks: mergeStringArray(defaults.footerPlatformLinks, incoming?.footerPlatformLinks),
    footerCompanyLinks: mergeStringArray(defaults.footerCompanyLinks, incoming?.footerCompanyLinks),
    footerLegalLinks: mergeStringArray(defaults.footerLegalLinks, incoming?.footerLegalLinks),
    pricingPlans: defaults.pricingPlans.map((fallback, index) => {
      const plan = Array.isArray(incoming?.pricingPlans) ? incoming!.pricingPlans[index] : undefined;
      return {
        ...fallback,
        ...(plan || {}),
        features: mergeStringArray(fallback.features, plan?.features),
      };
    }),
  };
}

function normalizeMarketingContent(value: unknown): MarketingContent {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_MARKETING_CONTENT;
    const result = parsed as Partial<MarketingContent>;
    return {
      ar: {
        ...DEFAULT_MARKETING_CONTENT.ar,
        ...(result.ar || {}),
        features: Array.isArray(result.ar?.features) && result.ar!.features.length
          ? result.ar!.features.slice(0, 3).map((feature, index) => ({ ...DEFAULT_MARKETING_CONTENT.ar.features[index], ...feature }))
          : DEFAULT_MARKETING_CONTENT.ar.features,
        stats: mergeLocaleArray(DEFAULT_MARKETING_CONTENT.ar.stats, result.ar?.stats),
        advancedFeatures: mergeLocaleArray(DEFAULT_MARKETING_CONTENT.ar.advancedFeatures, result.ar?.advancedFeatures),
        workflow: mergeLocaleArray(DEFAULT_MARKETING_CONTENT.ar.workflow, result.ar?.workflow),
        ui: mergeMarketingUi(DEFAULT_MARKETING_CONTENT.ar.ui, result.ar?.ui),
      },
      en: {
        ...DEFAULT_MARKETING_CONTENT.en,
        ...(result.en || {}),
        features: Array.isArray(result.en?.features) && result.en!.features.length
          ? result.en!.features.slice(0, 3).map((feature, index) => ({ ...DEFAULT_MARKETING_CONTENT.en.features[index], ...feature }))
          : DEFAULT_MARKETING_CONTENT.en.features,
        stats: mergeLocaleArray(DEFAULT_MARKETING_CONTENT.en.stats, result.en?.stats),
        advancedFeatures: mergeLocaleArray(DEFAULT_MARKETING_CONTENT.en.advancedFeatures, result.en?.advancedFeatures),
        workflow: mergeLocaleArray(DEFAULT_MARKETING_CONTENT.en.workflow, result.en?.workflow),
        ui: mergeMarketingUi(DEFAULT_MARKETING_CONTENT.en.ui, result.en?.ui),
      },
    };
  } catch {
    return DEFAULT_MARKETING_CONTENT;
  }
}

function mergeLegalLocale(defaults: LegalLocaleContent, incoming: Partial<LegalLocaleContent> | undefined): LegalLocaleContent {
  return {
    ...defaults,
    ...(incoming || {}),
    privacySections: Array.isArray(incoming?.privacySections) && incoming!.privacySections.length ? incoming!.privacySections : defaults.privacySections,
    termsSections: Array.isArray(incoming?.termsSections) && incoming!.termsSections.length ? incoming!.termsSections : defaults.termsSections,
  };
}

function normalizeLegalContent(value: unknown): LegalContent {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_LEGAL_CONTENT;
    const result = parsed as Partial<LegalContent>;
    return {
      ar: mergeLegalLocale(DEFAULT_LEGAL_CONTENT.ar, result.ar),
      en: mergeLegalLocale(DEFAULT_LEGAL_CONTENT.en, result.en),
    };
  } catch {
    return DEFAULT_LEGAL_CONTENT;
  }
}

function mergeContactLocale(defaults: ContactLocaleContent, incoming: Partial<ContactLocaleContent> | undefined): ContactLocaleContent {
  return {
    ...defaults,
    ...(incoming || {}),
    inquiryTypes: Array.isArray(incoming?.inquiryTypes) && incoming!.inquiryTypes.length ? incoming!.inquiryTypes : defaults.inquiryTypes,
  };
}

function normalizeContactPageContent(value: unknown): ContactPageContent {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_CONTACT_PAGE_CONTENT;
    const result = parsed as Partial<ContactPageContent>;
    return {
      ar: mergeContactLocale(DEFAULT_CONTACT_PAGE_CONTENT.ar, result.ar),
      en: mergeContactLocale(DEFAULT_CONTACT_PAGE_CONTENT.en, result.en),
    };
  } catch {
    return DEFAULT_CONTACT_PAGE_CONTENT;
  }
}

function mergeFaqLocale(defaults: FaqLocaleContent, incoming: Partial<FaqLocaleContent> | undefined): FaqLocaleContent {
  return {
    ...defaults,
    ...(incoming || {}),
    groups: Array.isArray(incoming?.groups) && incoming!.groups.length ? incoming!.groups : defaults.groups,
  };
}

function normalizeFaqContent(value: unknown): FaqContent {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_FAQ_CONTENT;
    const result = parsed as Partial<FaqContent>;
    return {
      ar: mergeFaqLocale(DEFAULT_FAQ_CONTENT.ar, result.ar),
      en: mergeFaqLocale(DEFAULT_FAQ_CONTENT.en, result.en),
    };
  } catch {
    return DEFAULT_FAQ_CONTENT;
  }
}

function mergeAboutLocale(defaults: AboutLocaleContent, incoming: Partial<AboutLocaleContent> | undefined): AboutLocaleContent {
  return {
    ...defaults,
    ...(incoming || {}),
    storyParagraphs: Array.isArray(incoming?.storyParagraphs) && incoming!.storyParagraphs.length ? incoming!.storyParagraphs : defaults.storyParagraphs,
    values: Array.isArray(incoming?.values) && incoming!.values.length ? incoming!.values : defaults.values,
    stats: Array.isArray(incoming?.stats) && incoming!.stats.length ? incoming!.stats : defaults.stats,
  };
}

function normalizeAboutContent(value: unknown): AboutContent {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_ABOUT_CONTENT;
    const result = parsed as Partial<AboutContent>;
    return {
      ar: mergeAboutLocale(DEFAULT_ABOUT_CONTENT.ar, result.ar),
      en: mergeAboutLocale(DEFAULT_ABOUT_CONTENT.en, result.en),
    };
  } catch {
    return DEFAULT_ABOUT_CONTENT;
  }
}

function mergeSeoLocale(incoming: Partial<SeoLocaleEntry> | undefined): SeoLocaleEntry {
  return {
    title: typeof incoming?.title === 'string' ? incoming.title : '',
    description: typeof incoming?.description === 'string' ? incoming.description : '',
  };
}

function mergeSeoPage(incoming: Partial<SeoPageSettings> | undefined): SeoPageSettings {
  return {
    ar: mergeSeoLocale(incoming?.ar),
    en: mergeSeoLocale(incoming?.en),
  };
}

/** إعدادات السيو كلها اختيارية (حقل فاضي = استخدم افتراضي الصفحة العادي)، فالتطبيع
 *  هنا بيقبل أي مجموعة فرعية من الصفحات/الحقول من غير ما يرفض القيمة كلها. */
function normalizeSeoSettings(value: unknown): SeoSettings {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object') return DEFAULT_SEO_SETTINGS;
    const result = parsed as Partial<Record<keyof SeoSettings, Partial<SeoPageSettings>>>;
    return {
      home: mergeSeoPage(result.home),
      about: mergeSeoPage(result.about),
      faq: mergeSeoPage(result.faq),
      contact: mergeSeoPage(result.contact),
      privacy: mergeSeoPage(result.privacy),
      terms: mergeSeoPage(result.terms),
    };
  } catch {
    return DEFAULT_SEO_SETTINGS;
  }
}

interface CustomizationContextValue {
  settings: SiteSettings;
  updateSettings: (newSettings: Partial<SiteSettings>) => Promise<void>;
  isLoading: boolean;
}

const CustomizationContext = createContext<CustomizationContextValue | undefined>(undefined);

/** تحسب تدريجات فاتحة وغامقة من لون أساسي */
function hexToHSL(hex: string): { h: number; s: number; l: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100; l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function generateLightVariant(hex: string): string {
  const hsl = hexToHSL(hex);
  if (!hsl) return hex;
  return hslToHex(hsl.h, Math.max(hsl.s - 20, 10), 95);
}

function generateHoverVariant(hex: string): string {
  const hsl = hexToHSL(hex);
  if (!hsl) return hex;
  return hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 8, 5));
}

function generateTone(hex: string, lightness: number, saturationShift = 0): string {
  const hsl = hexToHSL(hex);
  if (!hsl) return hex;
  return hslToHex(
    hsl.h,
    Math.min(100, Math.max(8, hsl.s + saturationShift)),
    Math.min(98, Math.max(4, lightness)),
  );
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return null;
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  };
}

function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const channel = (value: number) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };
  return (0.2126 * channel(rgb.r)) + (0.7152 * channel(rgb.g)) + (0.0722 * channel(rgb.b));
}

function contrastRatio(first: string, second: string): number {
  const firstLum = relativeLuminance(first);
  const secondLum = relativeLuminance(second);
  const brightest = Math.max(firstLum, secondLum);
  const darkest = Math.min(firstLum, secondLum);
  return (brightest + 0.05) / (darkest + 0.05);
}

function readableTextColor(background: string): '#FFFFFF' | '#071423' {
  return contrastRatio(background, '#FFFFFF') >= contrastRatio(background, '#071423')
    ? '#FFFFFF'
    : '#071423';
}

function injectGoogleFont(fontFamilyStr: string) {
  // استخراج اسم الخط من النص، مثلاً "'IBM Plex Sans Arabic', ..." -> "IBM Plex Sans Arabic"
  const match = fontFamilyStr.match(/^['"]?([^'",]+)['"]?/);
  if (!match) return;
  const fontName = match[1].trim();

  // لا حاجة لتحميل خطوط النظام
  if (['ui-sans-serif', 'system-ui', 'sans-serif'].includes(fontName)) return;

  const fontId = 'dynamic-google-font';
  let link = document.getElementById(fontId) as HTMLLinkElement;
  
  const href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, '+')}:wght@300;400;500;600;700;800;900&display=swap`;

  if (!link) {
    link = document.createElement('link');
    link.id = fontId;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  
  if (link.href !== href) {
    link.href = href;
  }
}

function applySettingsToDOM(settings: SiteSettings) {
  const root = document.documentElement;
  const primaryOnDark = generateTone(settings.primaryColor, 58, -28);
  const primaryOnDarkHover = generateTone(settings.primaryColor, 64, -24);
  const accentOnDark = generateTone(settings.accentColor, 68, -5);
  root.style.setProperty('--dyn-primary', settings.primaryColor);
  root.style.setProperty('--dyn-primary-hover', generateHoverVariant(settings.primaryColor));
  root.style.setProperty('--dyn-primary-light', generateLightVariant(settings.primaryColor));
  root.style.setProperty('--dyn-primary-soft', generateTone(settings.primaryColor, 96, -25));
  root.style.setProperty('--dyn-primary-muted', generateTone(settings.primaryColor, 89, -18));
  root.style.setProperty('--dyn-primary-strong', generateTone(settings.primaryColor, 22, 2));
  root.style.setProperty('--dyn-primary-contrast', readableTextColor(settings.primaryColor));
  root.style.setProperty('--dyn-primary-on-dark', primaryOnDark);
  root.style.setProperty('--dyn-primary-on-dark-hover', primaryOnDarkHover);
  root.style.setProperty('--dyn-primary-on-dark-contrast', readableTextColor(primaryOnDark));
  root.style.setProperty('--dyn-accent', settings.accentColor);
  root.style.setProperty('--dyn-accent-hover', generateHoverVariant(settings.accentColor));
  root.style.setProperty('--dyn-accent-light', generateLightVariant(settings.accentColor));
  root.style.setProperty('--dyn-accent-soft', generateTone(settings.accentColor, 96, -22));
  root.style.setProperty('--dyn-accent-muted', generateTone(settings.accentColor, 88, -15));
  root.style.setProperty('--dyn-accent-strong', generateTone(settings.accentColor, 34, 1));
  root.style.setProperty('--dyn-accent-contrast', readableTextColor(settings.accentColor));
  root.style.setProperty('--dyn-accent-on-dark', accentOnDark);
  root.style.setProperty('--dyn-accent-on-dark-contrast', readableTextColor(accentOnDark));
  root.style.setProperty('--dyn-font-family', settings.fontFamily);

  injectGoogleFont(settings.fontFamily);
}

export const CustomizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    try {
      const response = await api.getSiteSettings();
      if (response.settings) {
        const loaded: SiteSettings = {
          primaryColor: response.settings.primary_color || DEFAULT_SETTINGS.primaryColor,
          accentColor: response.settings.accent_color || DEFAULT_SETTINGS.accentColor,
          fontFamily: response.settings.font_family || DEFAULT_SETTINGS.fontFamily,
          marketingContent: normalizeMarketingContent(response.settings.marketing_content),
          legalContent: normalizeLegalContent(response.settings.legal_content),
          contactPageContent: normalizeContactPageContent(response.settings.contact_page_content),
          faqContent: normalizeFaqContent(response.settings.faq_content),
          aboutContent: normalizeAboutContent(response.settings.about_content),
          seoSettings: normalizeSeoSettings(response.settings.seo_settings),
        };
        setSettings(loaded);
        applySettingsToDOM(loaded);
      }
    } catch {
      // استخدام الإعدادات الافتراضية لو السيرفر مش متاح
      applySettingsToDOM(DEFAULT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateSettings = useCallback(async (newSettings: Partial<SiteSettings>) => {
    const merged = { ...settings, ...newSettings };
    setSettings(merged);
    applySettingsToDOM(merged);
    try {
      // لا نعيد إرسال محتوى الصفحات عند تعديل اللون أو الخط. هذا يمنع نسخة كود
      // جديدة أو تبويب قديم من استبدال النصوص المحفوظة في قاعدة البيانات دون قصد.
      const payload: Parameters<typeof api.adminUpdateSiteSettings>[0] = {};
      if (newSettings.primaryColor !== undefined) payload.primary_color = merged.primaryColor;
      if (newSettings.accentColor !== undefined) payload.accent_color = merged.accentColor;
      if (newSettings.fontFamily !== undefined) payload.font_family = merged.fontFamily;
      if (newSettings.marketingContent !== undefined) payload.marketing_content = JSON.stringify(merged.marketingContent);
      if (newSettings.legalContent !== undefined) payload.legal_content = JSON.stringify(merged.legalContent);
      if (newSettings.contactPageContent !== undefined) payload.contact_page_content = JSON.stringify(merged.contactPageContent);
      if (newSettings.faqContent !== undefined) payload.faq_content = JSON.stringify(merged.faqContent);
      if (newSettings.aboutContent !== undefined) payload.about_content = JSON.stringify(merged.aboutContent);
      if (newSettings.seoSettings !== undefined) payload.seo_settings = JSON.stringify(merged.seoSettings);
      await api.adminUpdateSiteSettings(payload);
    } catch (error) {
      setSettings(settings);
      applySettingsToDOM(settings);
      throw error;
    }
  }, [settings]);

  return (
    <CustomizationContext.Provider value={{ settings, updateSettings, isLoading }}>
      {children}
    </CustomizationContext.Provider>
  );
};

export function useCustomization(): CustomizationContextValue {
  const ctx = useContext(CustomizationContext);
  if (!ctx) throw new Error('useCustomization must be used within a CustomizationProvider');
  return ctx;
}
